import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Mail, Phone, MapPin, Briefcase, GraduationCap, Award, Calendar, Play, FileText } from 'lucide-react';

const CandidateViewPage = () => {
  const { userId } = useParams();
  const { token, user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios.get(`${API}/admin/candidates/${userId}`, { headers, withCredentials: true })
      .then(res => setCandidate(res.data))
      .catch(() => setCandidate(null))
      .finally(() => setLoading(false));
  }, [userId, token]);

  if (loading) {
    return <DashboardLayout><div className="flex items-center justify-center py-20"><div className="animate-spin w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full" /></div></DashboardLayout>;
  }

  if (!candidate) {
    return <DashboardLayout><div className="text-center py-20 text-slate-500">Candidate not found</div></DashboardLayout>;
  }

  const p = candidate.profile || {};
  const fullName = [p.first_name, p.last_name].filter(Boolean).join(' ') || candidate.name || candidate.email?.split('@')[0];
  const location = [p.city, p.state].filter(Boolean).join(', ');

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn" data-testid="candidate-view-page">
        <Button variant="ghost" className="gap-2 text-slate-500" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </Button>

        {/* Header Card */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start gap-5">
              <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-xl" style={{ fontFamily: 'Outfit' }}>
                {fullName.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }} data-testid="candidate-name">
                  {fullName}
                </h1>
                {p.current_designation && <p className="text-slate-500 mt-0.5">{p.current_designation}</p>}
                <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-600">
                  <span className="flex items-center gap-1.5"><Mail className="w-4 h-4 text-slate-400" />{candidate.email}</span>
                  {p.phone && <span className="flex items-center gap-1.5"><Phone className="w-4 h-4 text-slate-400" />{p.phone}</span>}
                  {location && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-slate-400" />{location}</span>}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-slate-500">Profile Completion</div>
                <div className="text-2xl font-bold text-indigo-600" style={{ fontFamily: 'Outfit' }}>
                  {p.profile_completion || 0}%
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Experience */}
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Briefcase className="w-4 h-4" /> Experience</CardTitle></CardHeader>
            <CardContent>
              {p.teaching_experience_years ? (
                <p className="text-slate-700"><strong>{p.teaching_experience_years}</strong> years of teaching experience</p>
              ) : <p className="text-slate-400 text-sm">Not provided</p>}
              {(p.work_experience || []).length > 0 && (
                <div className="mt-4 space-y-3">
                  {p.work_experience.map((w, i) => (
                    <div key={i} className="pl-3 border-l-2 border-indigo-200">
                      <p className="font-medium text-slate-900">{w.title || w.role}</p>
                      <p className="text-sm text-slate-500">{w.organization || w.company}</p>
                      <p className="text-xs text-slate-400">{w.start_year}–{w.end_year || 'Present'}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Education */}
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 text-base"><GraduationCap className="w-4 h-4" /> Education</CardTitle></CardHeader>
            <CardContent>
              {(p.education || []).length > 0 ? (
                <div className="space-y-3">
                  {p.education.map((e, i) => (
                    <div key={i} className="pl-3 border-l-2 border-green-200">
                      <p className="font-medium text-slate-900">{e.degree}</p>
                      <p className="text-sm text-slate-500">{e.institution}</p>
                      {e.year && <p className="text-xs text-slate-400">{e.year}</p>}
                    </div>
                  ))}
                </div>
              ) : <p className="text-slate-400 text-sm">Not provided</p>}
            </CardContent>
          </Card>

          {/* Skills */}
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Award className="w-4 h-4" /> Skills & Subjects</CardTitle></CardHeader>
            <CardContent>
              {(p.skills || []).length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {p.skills.map((s) => <Badge key={s} variant="secondary">{s}</Badge>)}
                </div>
              ) : <p className="text-slate-400 text-sm">No skills listed</p>}
              {(p.preferred_subjects || []).length > 0 && (
                <div className="mt-4">
                  <p className="text-xs text-slate-500 mb-2">Preferred Subjects</p>
                  <div className="flex flex-wrap gap-2">
                    {p.preferred_subjects.map((s) => <Badge key={s} variant="outline">{s}</Badge>)}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Certifications & Videos */}
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 text-base"><FileText className="w-4 h-4" /> Additional Info</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {(p.certifications || []).length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 mb-2">Certifications</p>
                  {p.certifications.map((c, i) => (
                    <div key={i} className="text-sm text-slate-700">{c.name} — {c.issuer} ({c.year})</div>
                  ))}
                </div>
              )}
              {p.video_resume_url && (
                <div>
                  <p className="text-xs text-slate-500 mb-2">Video Resume</p>
                  <a href={p.video_resume_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-indigo-600 text-sm hover:underline">
                    <Play className="w-4 h-4" /> Watch Video Resume
                  </a>
                </div>
              )}
              {p.resume_url && (
                <div>
                  <p className="text-xs text-slate-500 mb-2">Resume</p>
                  <a href={p.resume_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-indigo-600 text-sm hover:underline">
                    <FileText className="w-4 h-4" /> View Resume
                  </a>
                </div>
              )}
              <div>
                <p className="text-xs text-slate-500 mb-1">Registered</p>
                <p className="text-sm text-slate-700 flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-slate-400" />
                  {candidate.created_at ? new Date(candidate.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) : '—'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CandidateViewPage;
